/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Nucleus Admin API
 * Headless, multi-tenant commerce backend for jewelry brands.
 *
 * ## Surfaces
 *
 * | Prefix | Caller | Credential |
 * |---|---|---|
 * | `/platform/v1` | Platform operator | Platform token from **Platform: Auth** |
 * | `/admin/v1` | Staff of one organization | Staff token from **Admin: Auth** |
 * | `/storefront/v1` | A storefront | Store API key from **Admin: API keys** |
 * | `/public/v1` | Anyone (the brand application form) | None; rate limited per IP |
 *
 * Every credential is sent as `Authorization: Bearer <value>`. Use the **Authorize** button once and it applies to every request you try here.
 *
 * ## Conventions
 *
 * - **Ids** are UUID v7 strings. **Timestamps** are ISO 8601 in UTC. **Money** is `{ amount, currency }` with `amount` in integer minor units.
 * - **Lists** paginate by cursor: `?limit=20&cursor=...` in, `{ items, nextCursor }` out, `nextCursor` is `null` on the last page.
 * - **Errors** always use one envelope, `{ error: { code, message, requestId, details?, meta? } }`. Switch on `code`, never on `message`.
 * - **Request ids**: every response carries `x-request-id`. Quote it when reporting a problem.
 * - **Idempotency**: endpoints that create money-related state require an `Idempotency-Key` header; the header is listed on those endpoints.
 * - A record that belongs to another tenant answers **404**, never 403, so existence is not leaked.
 * OpenAPI spec version: 0.1.0
 */
import type {
  AssignConversationPathParameters,
  AssignConversationRequest,
  ChatDiscountOptions,
  ChatDraftOrder,
  ChatPresenceList,
  ChatSocketTicket,
  ChatVisitorProfile,
  CloseConversationPathParameters,
  ConversationContextDetail,
  ConversationCounts,
  ConversationDetail,
  ConvertConversationPathParameters,
  ConvertConversationRequest,
  CreateChatDraftOrderRequest,
  CreateConversationDraftOrderPathParameters,
  CustomerUpload,
  GetChatVisitorPathParameters,
  GetConversationAttachmentPathParameters,
  GetConversationContextPathParameters,
  GetConversationPathParameters,
  IssueChatDiscountRequest,
  IssueConversationDiscountPathParameters,
  JoinConversationPathParameters,
  LeaveConversationPathParameters,
  ListChatDiscountsPathParameters,
  ListChatPresencePathParameters,
  ListConversationMessagesParams,
  ListConversationMessagesPathParameters,
  ListConversations200,
  ListConversationsParams,
  ListConversationsPathParameters,
  ListLiveChatVisitorsPathParameters,
  ListOrgConversations200,
  ListOrgConversationsParams,
  ListOrgLiveChatVisitorsParams,
  LiveChatVisitorList,
  MarkConversationReadPathParameters,
  ProactiveConversation,
  ReadChatMessagesRequest,
  ReopenConversationPathParameters,
  SendChatInvoiceRequest,
  SendConversationInvoicePathParameters,
  SendConversationMessagePathParameters,
  SendStaffChatMessageRequest,
  StaffChatMessage,
  StaffChatMessagePage,
  StartProactiveConversationPathParameters,
  StartProactiveConversationRequest,
  TrashConversationPathParameters,
  UpdateConversationPathParameters,
  UpdateConversationRequest,
  UploadConversationAttachmentParams,
  UploadConversationAttachmentPathParameters
} from '../model';

import { customInstance } from '../../../transport/mutator';
import type { BodyType } from '../../../transport/mutator';


type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];


  /**
 * @summary Conversations of the store, latest activity first
 */
export const listConversations = (
    { storeId }: ListConversationsPathParameters,
    params?: ListConversationsParams,
 options?: SecondParameter<typeof customInstance<ListConversations200>>,) => {
      return customInstance<ListConversations200>(
      {url: `/admin/v1/stores/${storeId}/conversations`, method: 'GET',
        params
    },
      options);
    }
  /**
 * Every store the member can access, one list ordered by latest activity; storeId narrows it. Each row carries its storeId for the store badge
 * @summary The organisation-wide inbox
 */
export const listOrgConversations = (
    params?: ListOrgConversationsParams,
 options?: SecondParameter<typeof customInstance<ListOrgConversations200>>,) => {
      return customInstance<ListOrgConversations200>(
      {url: `/admin/v1/org/conversations`, method: 'GET',
        params
    },
      options);
    }
  /**
 * @summary Open, pending, unassigned and mine per store
 */
export const getConversationCounts = (

 options?: SecondParameter<typeof customInstance<ConversationCounts>>,) => {
      return customInstance<ConversationCounts>(
      {url: `/admin/v1/org/conversations/counts`, method: 'GET'
    },
      options);
    }
  /**
 * @summary One conversation with its participants
 */
export const getConversation = (
    { storeId, id }: GetConversationPathParameters,
 options?: SecondParameter<typeof customInstance<ConversationDetail>>,) => {
      return customInstance<ConversationDetail>(
      {url: `/admin/v1/stores/${storeId}/conversations/${id}`, method: 'GET'
    },
      options);
    }
  /**
 * tags replaces the list; the thread records what was added and removed
 * @summary Tags, priority or subject
 */
export const updateConversation = (
    { storeId, id }: UpdateConversationPathParameters,
    updateConversationRequest: BodyType<UpdateConversationRequest>,
 options?: SecondParameter<typeof customInstance<ConversationDetail>>,) => {
      return customInstance<ConversationDetail>(
      {url: `/admin/v1/stores/${storeId}/conversations/${id}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: updateConversationRequest
    },
      options);
    }
  /**
 * It leaves the inbox at once (a conversation.updated frame with trashed: true) and the shopper's conversation list, and is deleted with its files after 30 days
 * @summary Move a conversation to the trash
 */
export const trashConversation = (
    { storeId, id }: TrashConversationPathParameters,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/stores/${storeId}/conversations/${id}`, method: 'DELETE'
    },
      options);
    }
  /**
 * Without after or before: the latest page. after=<last id> resumes after a reconnect; before scrolls back
 * @summary The thread, oldest first, whispers and events included
 */
export const listConversationMessages = (
    { storeId, id }: ListConversationMessagesPathParameters,
    params?: ListConversationMessagesParams,
 options?: SecondParameter<typeof customInstance<StaffChatMessagePage>>,) => {
      return customInstance<StaffChatMessagePage>(
      {url: `/admin/v1/stores/${storeId}/conversations/${id}/messages`, method: 'GET',
        params
    },
      options);
    }
  /**
 * A reply makes the author a participant, records the first response, moves open to pending, marks the shopper's messages read and claims an unassigned conversation. isInternal whispers do none of that and never reach the shopper. 409 CONVERSATION_CLOSED: reopen first
 * @summary Reply, whisper or send a card
 */
export const sendConversationMessage = (
    { storeId, id }: SendConversationMessagePathParameters,
    sendStaffChatMessageRequest: BodyType<SendStaffChatMessageRequest>,
 options?: SecondParameter<typeof customInstance<StaffChatMessage>>,) => {
      return customInstance<StaffChatMessage>(
      {url: `/admin/v1/stores/${storeId}/conversations/${id}/messages`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: sendStaffChatMessageRequest
    },
      options);
    }
  /**
 * userId must be active staff with access to the store; null unassigns. The thread records assigned, transferred or unassigned as a staff-only event
 * @summary Assign, transfer or unassign, or move to a department
 */
export const assignConversation = (
    { storeId, id }: AssignConversationPathParameters,
    assignConversationRequest: BodyType<AssignConversationRequest>,
 options?: SecondParameter<typeof customInstance<ConversationDetail>>,) => {
      return customInstance<ConversationDetail>(
      {url: `/admin/v1/stores/${storeId}/conversations/${id}/assign`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: assignConversationRequest
    },
      options);
    }
  /**
 * Joining twice is a no-op
 * @summary Join as a participant
 */
export const joinConversation = (
    { storeId, id }: JoinConversationPathParameters,
 options?: SecondParameter<typeof customInstance<ConversationDetail>>,) => {
      return customInstance<ConversationDetail>(
      {url: `/admin/v1/stores/${storeId}/conversations/${id}/join`, method: 'POST'
    },
      options);
    }
  /**
 * The assignment stays; 409 when not a participant
 * @summary Leave the conversation
 */
export const leaveConversation = (
    { storeId, id }: LeaveConversationPathParameters,
 options?: SecondParameter<typeof customInstance<ConversationDetail>>,) => {
      return customInstance<ConversationDetail>(
      {url: `/admin/v1/stores/${storeId}/conversations/${id}/leave`, method: 'POST'
    },
      options);
    }
  /**
 * The shopper's next message then starts a new conversation. 409 when already closed
 * @summary Close the conversation
 */
export const closeConversation = (
    { storeId, id }: CloseConversationPathParameters,
 options?: SecondParameter<typeof customInstance<ConversationDetail>>,) => {
      return customInstance<ConversationDetail>(
      {url: `/admin/v1/stores/${storeId}/conversations/${id}/close`, method: 'POST'
    },
      options);
    }
  /**
 * @summary Reopen a closed conversation
 */
export const reopenConversation = (
    { storeId, id }: ReopenConversationPathParameters,
 options?: SecondParameter<typeof customInstance<ConversationDetail>>,) => {
      return customInstance<ConversationDetail>(
      {url: `/admin/v1/stores/${storeId}/conversations/${id}/reopen`, method: 'POST'
    },
      options);
    }
  /**
 * Same conversation, number and thread; the thread records a staff-only converted event. SLA targets from settings.chat.sla are dated from when the conversation started, in business hours. From then on every staff reply the shopper can see is emailed to them. email is used only when the conversation has none. 409 when it is already a ticket
 * @summary Turn a chat or offline message into a ticket
 */
export const convertConversation = (
    { storeId, id }: ConvertConversationPathParameters,
    convertConversationRequest: BodyType<ConvertConversationRequest>,
 options?: SecondParameter<typeof customInstance<ConversationDetail>>,) => {
      return customInstance<ConversationDetail>(
      {url: `/admin/v1/stores/${storeId}/conversations/${id}/convert`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: convertConversationRequest
    },
      options);
    }
  /**
 * @summary Mark the shopper's messages read up to an id
 */
export const markConversationRead = (
    { storeId, id }: MarkConversationReadPathParameters,
    readChatMessagesRequest: BodyType<ReadChatMessagesRequest>,
 options?: SecondParameter<typeof customInstance<ConversationDetail>>,) => {
      return customInstance<ConversationDetail>(
      {url: `/admin/v1/stores/${storeId}/conversations/${id}/read`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: readChatMessagesRequest
    },
      options);
    }
  /**
 * Raw body with the file's Content-Type (image/jpeg, image/png, image/webp, image/heic, image/heif, application/pdf), same sniffing and size limit as shopper uploads. Put the token in the message's attachments
 * @summary Upload a file to send in this conversation
 */
export const uploadConversationAttachment = (
    { storeId, id }: UploadConversationAttachmentPathParameters,
    params: UploadConversationAttachmentParams,
 options?: SecondParameter<typeof customInstance<CustomerUpload>>,) => {
      return customInstance<CustomerUpload>(
      {url: `/admin/v1/stores/${storeId}/conversations/${id}/attachments`, method: 'POST',
        params
    },
      options);
    }
  /**
 * Images inline, PDFs as an attachment; never cached
 * @summary Download a file from the thread
 */
export const getConversationAttachment = (
    { storeId, id, uploadId }: GetConversationAttachmentPathParameters,
 options?: SecondParameter<typeof customInstance<string>>,) => {
      return customInstance<string>(
      {url: `/admin/v1/stores/${storeId}/conversations/${id}/attachments/${uploadId}`, method: 'GET'
    },
      options);
    }
  /**
 * @summary A visitor's profile, attributes, current visit with the pages opened, and latest conversations
 */
export const getChatVisitor = (
    { storeId, id }: GetChatVisitorPathParameters,
 options?: SecondParameter<typeof customInstance<ChatVisitorProfile>>,) => {
      return customInstance<ChatVisitorProfile>(
      {url: `/admin/v1/stores/${storeId}/chat/visitors/${id}`, method: 'GET'
    },
      options);
    }
  /**
 * Members who can reply and hold an inbox socket, with their online or away status. Changes arrive live as presence.updated on the inbox socket
 * @summary Staff online for this store's chat
 */
export const listChatPresence = (
    { storeId }: ListChatPresencePathParameters,
 options?: SecondParameter<typeof customInstance<ChatPresenceList>>,) => {
      return customInstance<ChatPresenceList>(
      {url: `/admin/v1/stores/${storeId}/chat/presence`, method: 'GET'
    },
      options);
    }
  /**
 * Visitors whose widget sent a presence heartbeat in the last 90 seconds, grouped chatting, active and idle. Changes arrive as visitor.updated on the inbox socket, at most one every 10 seconds per visitor; a visitor who leaves simply stops appearing, so refetch every 30 seconds or so. Banned visitors are never listed
 * @summary Shoppers on the storefront right now
 */
export const listLiveChatVisitors = (
    { storeId }: ListLiveChatVisitorsPathParameters,
 options?: SecondParameter<typeof customInstance<LiveChatVisitorList>>,) => {
      return customInstance<LiveChatVisitorList>(
      {url: `/admin/v1/stores/${storeId}/chat/visitors`, method: 'GET'
    },
      options);
    }
  /**
 * The live visitor list across every store the member can access (all stores when the membership is unrestricted), each row with its storeId; storeId narrows it to one store and answers 404 outside the member's access. Same grouping and ordering as the per-store list, counts over every listed store, at most 500 rows. visitor.updated frames carry storeId too
 * @summary Shoppers on any of your stores' storefronts right now
 */
export const listOrgLiveChatVisitors = (
    params?: ListOrgLiveChatVisitorsParams,
 options?: SecondParameter<typeof customInstance<LiveChatVisitorList>>,) => {
      return customInstance<LiveChatVisitorList>(
      {url: `/admin/v1/org/chat/visitors`, method: 'GET',
        params
    },
      options);
    }
  /**
 * Opens a pending conversation assigned to you with your message first; the shopper's widget opens on it when their socket is connected, otherwise on the next poll. 409 when the visitor already has an open conversation (meta.conversationId), 403 VISITOR_BANNED for a banned visitor
 * @summary Start a chat with a visitor
 */
export const startProactiveConversation = (
    { storeId, id }: StartProactiveConversationPathParameters,
    startProactiveConversationRequest: BodyType<StartProactiveConversationRequest>,
 options?: SecondParameter<typeof customInstance<ProactiveConversation>>,) => {
      return customInstance<ProactiveConversation>(
      {url: `/admin/v1/stores/${storeId}/chat/visitors/${id}/conversations`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: startProactiveConversationRequest
    },
      options);
    }
  /**
 * One call. The cart is re-priced now and the product is read as the storefront shows it; orders carry the production stage; pages is the visitor's trail this visit. Customer fields are null for a visitor with no account
 * @summary The sidebar next to a thread: visitor, customer, cart, product, orders, history
 */
export const getConversationContext = (
    { storeId, id }: GetConversationContextPathParameters,
 options?: SecondParameter<typeof customInstance<ConversationContextDetail>>,) => {
      return customInstance<ConversationContextDetail>(
      {url: `/admin/v1/stores/${storeId}/conversations/${id}/context`, method: 'GET'
    },
      options);
    }
  /**
 * Active code discounts of the store (no discounts.read needed) with the caller's daily allowance, counted in the store's calendar day
 * @summary Discounts you can hand out in chat, and how many codes you have left today
 */
export const listChatDiscounts = (
    { storeId }: ListChatDiscountsPathParameters,
 options?: SecondParameter<typeof customInstance<ChatDiscountOptions>>,) => {
      return customInstance<ChatDiscountOptions>(
      {url: `/admin/v1/stores/${storeId}/chat/discounts`, method: 'GET'
    },
      options);
    }
  /**
 * Draws a one-use code (CHAT-…) on an active code discount and posts it as a discount card, counted as a reply. Each member may issue settings.chat.behaviour.discountCapPerAgentPerDay codes per store day: past it 422 LIMIT_EXCEEDED with meta { limit, usedToday, resetsAt }. Audited. 409 for a closed conversation or a discount that is not active
 * @summary Hand the shopper a single-use code
 */
export const issueConversationDiscount = (
    { storeId, id }: IssueConversationDiscountPathParameters,
    issueChatDiscountRequest: BodyType<IssueChatDiscountRequest>,
 options?: SecondParameter<typeof customInstance<StaffChatMessage>>,) => {
      return customInstance<StaffChatMessage>(
      {url: `/admin/v1/stores/${storeId}/conversations/${id}/discount`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: issueChatDiscountRequest
    },
      options);
    }
  /**
 * Needs orders.write and conversations.write. A draft order for the conversation's customer (else its email) with one line per product card in the thread and an optional deposit line, created even when nothing could go on it; cards that need a variant or a customization (ring size) picked, or are not for sale, are listed in skipped for staff to add on the draft page. The conversation remembers it as context.draftOrderId. Edit it like any draft, then send the invoice from here
 * @summary Start a quote from the conversation
 */
export const createConversationDraftOrder = (
    { storeId, id }: CreateConversationDraftOrderPathParameters,
    createChatDraftOrderRequest: BodyType<CreateChatDraftOrderRequest>,
 options?: SecondParameter<typeof customInstance<ChatDraftOrder>>,) => {
      return customInstance<ChatDraftOrder>(
      {url: `/admin/v1/stores/${storeId}/conversations/${id}/draft-order`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: createChatDraftOrderRequest
    },
      options);
    }
  /**
 * Needs orders.write and conversations.write. Sends the draft order's invoice email exactly like the draft orders page does, then posts an invoice card (lines, total, where it was emailed). Defaults to the conversation's latest draft. 409 when the draft cannot be invoiced (meta.blockers) or the conversation is closed
 * @summary Send the quote's invoice and show it in the thread
 */
export const sendConversationInvoice = (
    { storeId, id }: SendConversationInvoicePathParameters,
    sendChatInvoiceRequestNull: BodyType<SendChatInvoiceRequest | null>,
 options?: SecondParameter<typeof customInstance<StaffChatMessage>>,) => {
      return customInstance<StaffChatMessage>(
      {url: `/admin/v1/stores/${storeId}/conversations/${id}/invoice`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: sendChatInvoiceRequestNull
    },
      options);
    }
  /**
 * One socket for every store you can access: inbox rows, presence and the threads you subscribe to. Members who can reply count as online for their stores while it is open; send a presence frame to switch between online and away. Open the url within 30 seconds; the ticket works once. Close codes as on the storefront socket
 * @summary Ticket for the inbox WebSocket
 */
export const createInboxSocketTicket = (

 options?: SecondParameter<typeof customInstance<ChatSocketTicket>>,) => {
      return customInstance<ChatSocketTicket>(
      {url: `/admin/v1/chat/ws-ticket`, method: 'POST'
    },
      options);
    }
  export type ListConversationsResult = NonNullable<Awaited<ReturnType<typeof listConversations>>>
export type ListOrgConversationsResult = NonNullable<Awaited<ReturnType<typeof listOrgConversations>>>
export type GetConversationCountsResult = NonNullable<Awaited<ReturnType<typeof getConversationCounts>>>
export type GetConversationResult = NonNullable<Awaited<ReturnType<typeof getConversation>>>
export type UpdateConversationResult = NonNullable<Awaited<ReturnType<typeof updateConversation>>>
export type TrashConversationResult = NonNullable<Awaited<ReturnType<typeof trashConversation>>>
export type ListConversationMessagesResult = NonNullable<Awaited<ReturnType<typeof listConversationMessages>>>
export type SendConversationMessageResult = NonNullable<Awaited<ReturnType<typeof sendConversationMessage>>>
export type AssignConversationResult = NonNullable<Awaited<ReturnType<typeof assignConversation>>>
export type JoinConversationResult = NonNullable<Awaited<ReturnType<typeof joinConversation>>>
export type LeaveConversationResult = NonNullable<Awaited<ReturnType<typeof leaveConversation>>>
export type CloseConversationResult = NonNullable<Awaited<ReturnType<typeof closeConversation>>>
export type ReopenConversationResult = NonNullable<Awaited<ReturnType<typeof reopenConversation>>>
export type ConvertConversationResult = NonNullable<Awaited<ReturnType<typeof convertConversation>>>
export type MarkConversationReadResult = NonNullable<Awaited<ReturnType<typeof markConversationRead>>>
export type UploadConversationAttachmentResult = NonNullable<Awaited<ReturnType<typeof uploadConversationAttachment>>>
export type GetConversationAttachmentResult = NonNullable<Awaited<ReturnType<typeof getConversationAttachment>>>
export type GetChatVisitorResult = NonNullable<Awaited<ReturnType<typeof getChatVisitor>>>
export type ListChatPresenceResult = NonNullable<Awaited<ReturnType<typeof listChatPresence>>>
export type ListLiveChatVisitorsResult = NonNullable<Awaited<ReturnType<typeof listLiveChatVisitors>>>
export type ListOrgLiveChatVisitorsResult = NonNullable<Awaited<ReturnType<typeof listOrgLiveChatVisitors>>>
export type StartProactiveConversationResult = NonNullable<Awaited<ReturnType<typeof startProactiveConversation>>>
export type GetConversationContextResult = NonNullable<Awaited<ReturnType<typeof getConversationContext>>>
export type ListChatDiscountsResult = NonNullable<Awaited<ReturnType<typeof listChatDiscounts>>>
export type IssueConversationDiscountResult = NonNullable<Awaited<ReturnType<typeof issueConversationDiscount>>>
export type CreateConversationDraftOrderResult = NonNullable<Awaited<ReturnType<typeof createConversationDraftOrder>>>
export type SendConversationInvoiceResult = NonNullable<Awaited<ReturnType<typeof sendConversationInvoice>>>
export type CreateInboxSocketTicketResult = NonNullable<Awaited<ReturnType<typeof createInboxSocketTicket>>>
